import { useCallback, useState } from "react"
import ModernMUITable from "../../../Components/ModernMUITable"
import { useModalProvider } from "../../../Providers/ModalProvider"
import CreateProjectModal from "./Subcomponents/CreateProjectModal"
import ModifyProjectModal from "./Subcomponents/ModifyProjectModal"
import { useEndpointProvider } from "../../../Providers/EndpointProvider"
import EyeIcon from "../../../Components/Icons/EyeIcon"
import { MdOutlineModeEdit } from "react-icons/md"
import { useBackendLinksProvider } from "../../../Providers/BackendLinksProvider"
import { GridActionsCellItem, GRID_CHECKBOX_SELECTION_COL_DEF } from "@mui/x-data-grid"
import { MobergButton, MobergButtonShape, MobergButtonVariant, MobergTheme } from "../../../Moberg"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import ConfirmationModal from "../../../Components/ConfirmationModal/ConfirmationModal"
import { useAuthProvider } from "../../../Providers/AuthProvider"
import { permissionTooltip } from "../../../Utils/permissionUtils"

export default function Studies() {
	const { createModal, close } = useModalProvider()
	const endpointProvider = useEndpointProvider()
	const { LINKS } = useBackendLinksProvider()
	const queryClient = useQueryClient()
	const authProvider = useAuthProvider()
	const canManageProjects = authProvider.permissions?.['project:manage']

	// Row selection state for delete functionality
	const [selectedProjectIds, setSelectedProjectIds] = useState([])

	const deleteDisabledReason = permissionTooltip(canManageProjects)
		|| (selectedProjectIds.length === 0 ? "Select projects to delete." : "")

	// Pagination state
	const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 })

	const handleClick = useCallback((row, viewMode = false) => {
		createModal(<ModifyProjectModal style={{ height: "80%" }} close={close} row={row} escClose={false} clickOutsideClose={false} viewMode={viewMode} />)
	}, [close, createModal])

	// Delete projects mutation
	const deleteMutation = useMutation({
		mutationFn: async (project_ids) => {
			return endpointProvider.post(LINKS.ADMIN.PROJECTS.DELETE_PROJECTS, { project_ids })
		},
		onSuccess: () => {
			setSelectedProjectIds([])
			queryClient.invalidateQueries(["projects"])
		}
	})

	const handleDeleteProjects = () => {
		if (selectedProjectIds.length > 0) {
			createModal(<ConfirmationModal
				title="Delete projects?"
				description={`Are you sure you want to permanently delete ${selectedProjectIds.length} project${selectedProjectIds.length > 1 ? 's' : ''}? This action cannot be undone.`}
				confirmButton={{
					text: "Yes, delete",
					theme: MobergTheme.RED,
					onClick: () => deleteMutation.mutate(selectedProjectIds)
				}}
			/>)
		}
	}



	const columns = [
		{
			...GRID_CHECKBOX_SELECTION_COL_DEF,
			hideable: false,
		},
		{ field: "identifier", flex: 0.4, headerName: "Project ID", minWidth: 90, visible: true },
		{ field: "name", flex: 0.4, headerName: "Name", minWidth: 70, visible: true },
		{ field: "bucket_name", flex: 0.4, headerName: "Repository", minWidth: 115, visible: true },
		{ field: "user_count", flex: 0.4, headerName: "No. users", valueGetter: (value, row) => row?.user_count, minWidth: 110, visible: true },
		{ field: "patient_count", flex: 0.4, headerName: "No. patients", valueGetter: (value, row) => row?.patient_count, minWidth: 110, visible: true },
		{
			field: "actions",
			flex: 0.4,
			headerName: "Actions",
			minWidth: 120,
			hideable: false,
			type: "actions",
			getActions: params => [
				<GridActionsCellItem icon={<EyeIcon />} label="View" onClick={() => handleClick(params.row, true)} showInMenu />,
				<GridActionsCellItem icon={<MdOutlineModeEdit size={20} />} label="Edit" onClick={() => handleClick(params.row)} showInMenu disabled={!canManageProjects} />,
			],
			visible: true,
		},
	]

	function renderProjectModal() {
		createModal(<CreateProjectModal style={{ height: "80%" }} close={close} escClose={false} clickOutsideClose={false} />)
	}

	const projectTableToolbarProps = {
		searchInput: { isShow: true },
		columnButton: { isShow: true },
		mainTable: true
	}

	const queryProps = {
		queryKey: "projects",
		endpoint: LINKS.ADMIN.PROJECTS.GET_PROJECTS,
	}

	const [columnVisibilityModel, setColumnVisibilityModel] = useState({});

	return (
		<div style={{ 
			position: 'relative', 
			padding: '10px 30px 20px', 
			height: 'calc(100vh - 200px)',
			minHeight: '500px',
			maxHeight: '80vh'
		}}>
			{/* Action buttons above table */}
			<div style={{ paddingBottom: '10px', display: 'flex', gap: '12px', alignItems: 'center' }}>
				<MobergButton
					variant={MobergButtonVariant.FILLED}
					theme={MobergTheme.BLUE}
					shape={MobergButtonShape.RECTANGLE}
					onClick={renderProjectModal}
					disabled={!canManageProjects}
					tooltip={permissionTooltip(canManageProjects)}
				>
					Create project
				</MobergButton>
				
				<MobergButton
					variant={MobergButtonVariant.FILLED}
					theme={MobergTheme.RED}
					shape={MobergButtonShape.RECTANGLE}
					onClick={handleDeleteProjects}
					disabled={!!deleteDisabledReason}
					tooltip={deleteDisabledReason}
				>
					Delete ({selectedProjectIds.length})
				</MobergButton>
			</div>

			<ModernMUITable 
				columns={columns} 
				tableToolbarProps={projectTableToolbarProps} 
				{...queryProps} 
				paginationMode="server"
				sortingMode="server"
			paginationModel={paginationModel}
			onPaginationModelChange={setPaginationModel}
			supportsPageSize={true}
				height="100%"
				columnVisibilityModel={columnVisibilityModel}
				onColumnVisibilityModelChange={setColumnVisibilityModel}
				enableRowSelection={true}
				onSelectionChange={(ids) => setSelectedProjectIds(ids)}
				isRowSelectable={() => canManageProjects}
				persistStateInUrl={true}
				tableId="projects"
				defaultSortModel={[
					{ field: 'name', sort: 'asc' }
				]}
			/>
		</div>
	)
}
